import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { CaseMoney, Money } from '@/types/domain'

import { signedNumber } from './money'

/** One footer balance: eyebrow, figure, and the line saying where it came from. */
export function BalanceCell({
  label,
  amount,
  note,
  tone,
}: {
  label: string
  amount?: Money
  note?: string
  tone?: 'attention'
}) {
  return (
    <div className="bg-surface flex flex-col gap-1.5 px-[18px] py-4">
      <span className="asa-eyebrow">{label}</span>
      <span
        className={cn(
          'asa-num text-[17px] leading-none font-semibold',
          tone === 'attention' ? 'text-attention' : 'text-text-heading',
        )}
      >
        {amount ? (
          <>
            {tone === 'attention' ? signedNumber(amount.amount) : formatNumber(amount.amount, 2)}
            <span className="text-text-muted ml-1 text-[12px] font-normal">
              {amount.currency}
            </span>
          </>
        ) : (
          '—'
        )}
      </span>
      {note ? (
        <span className="text-text-muted text-[12px] leading-snug font-light">{note}</span>
      ) : null}
    </div>
  )
}

/** The three balances the table produces: what the client still owes, what we
 *  owe vendors, and the extra charge waiting on a decision.
 *
 *  The two notes arrive already worded rather than derived here: both read the
 *  session's locale through the section's `useFormat`, and a second call to it
 *  down here would be a second subscription for the same sentence. */
export function BalancesRow({
  money,
  trancheNote,
  payablesNote,
}: {
  money: CaseMoney
  trancheNote?: string
  payablesNote?: string
}) {
  const { t } = useTranslation()
  const pending = money.pendingApproval

  return (
    /* gap-px over a hairline ground rules the cells in both axes without
       per-index border arithmetic (same idiom as the dashboard tiles). */
    <div className="bg-line-hair border-line-hair grid grid-cols-1 gap-px border-t sm:grid-cols-3">
      <BalanceCell
        label={t('cases.money.clientReceivable')}
        amount={money.clientReceivable}
        note={trancheNote || undefined}
      />
      <BalanceCell
        label={t('cases.money.payables')}
        amount={money.payables}
        note={payablesNote || undefined}
      />
      <BalanceCell
        label={t('cases.money.pendingApprovalLabel')}
        amount={pending?.delta}
        note={pending ? t('cases.money.pendingNote') : t('cases.money.noPending')}
        tone={pending ? 'attention' : undefined}
      />
    </div>
  )
}

/** FR-30's three figures, computed server-side and never stored (AD-8).
 *
 *  Three cells rather than one "profit", because they answer three different
 *  questions and a single label would be the exact confusion AD-8 forbids: what
 *  the deal was signed to earn, what it is now tracking to earn, and what the
 *  cash says it did earn.
 *
 *  `hasRate` is what keeps the zeros honest. A case nobody has priced yet has a
 *  real expected profit — the negative of its planned costs — and saying so
 *  under the figure beats implying a rate of nil. */
export function ProfitRow({ money }: { money: CaseMoney }) {
  const { t } = useTranslation()

  return (
    <div className="bg-line-hair border-line-hair grid grid-cols-1 gap-px border-t sm:grid-cols-3">
      <BalanceCell
        label={t('cases.money.expectedProfit')}
        amount={money.expectedProfit}
        note={
          money.hasRate === false
            ? t('cases.money.noRateYet')
            : t('cases.money.expectedProfitNote')
        }
      />
      <BalanceCell
        label={t('cases.money.forecastProfitLabel')}
        amount={money.forecastProfit}
        note={t('cases.money.forecastProfitNote')}
      />
      <BalanceCell
        label={t('cases.money.finalProfit')}
        amount={money.finalProfit}
        note={t('cases.money.finalProfitNote')}
      />
    </div>
  )
}
